'use client';

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../../utils/cn';
import styles from './bottom-sheets.module.css';
import type { BottomSheetsProps, BottomSheetsMethods } from './bottom-sheets.types';
import Text from '../../atoms/text';
import Button from '../../atoms/button';
import { BOTTOM_SHEET_HEIGHT } from './bottom-sheets.constants';
import { IconCloseBottomSheet } from '../../atoms/icons/icon-close-bottom-sheet';
const BottomSheets = forwardRef<BottomSheetsMethods, BottomSheetsProps>(
    (
        {
            isOpen: controlledIsOpen,
            title,
            titleProps,
            children,
            childrenClassName,
            footer,
            footerClassName,
            height = BOTTOM_SHEET_HEIGHT.AUTO,
            dismissOnBackdrop = true,
            onClose,
            iconProps,
            containerClassName,
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = useState(!!controlledIsOpen);
        const [mounted, setMounted] = useState(!!controlledIsOpen);
        const [visible, setVisible] = useState(false);
        useEffect(() => {
            if (isOpen) {
                setMounted(true);
                setVisible(false);

                const timer = setTimeout(() => {
                    setVisible(true);
                }, 0);

                return () => clearTimeout(timer);
            } else {
                setVisible(false);
                const timer = setTimeout(() => setMounted(false), 500);
                return () => clearTimeout(timer);
            }
        }, [isOpen]);

        useEffect(() => {
            if (controlledIsOpen !== undefined) {
                setIsOpen(controlledIsOpen);
            }
        }, [controlledIsOpen]);

        useEffect(() => {
            document.body.style.overflow = isOpen ? 'hidden' : '';
            return () => {
                document.body.style.overflow = '';
            };
        }, [isOpen]);

        const handleClose = () => {
            setIsOpen(false);
            onClose?.();
        };

        useImperativeHandle(ref, () => ({
            open: () => setIsOpen(true),
            close: handleClose,
        }));

        if (!mounted) return null;

        return (
            <div>
                {/* Backdrop */}
                <button
                    type="button"
                    aria-label="Close bottom sheet"
                    className={cn(styles.backdrop, visible ? styles.backdropOpen : styles.backdropClosed)}
                    onClick={dismissOnBackdrop ? handleClose : undefined}
                />

                {/* Sheet */}
                <div
                    className={cn(
                        styles.sheet,
                        styles[height],
                        visible ? styles.open : styles.closed,
                        containerClassName
                    )}
                >
                    {/* Close button */}
                    <Button onClick={handleClose} className={styles.buttonClose}>
                        <IconCloseBottomSheet {...iconProps} className={cn(styles.iconButton, iconProps?.className)} />
                    </Button>
                    <Text showIf={!!title} {...titleProps} className={cn(styles.title, titleProps?.className)}>
                        {title}
                    </Text>
                    {/* Content */}
                    <div className={cn(styles.content, childrenClassName)}>{children}</div>
                    {/* Footer */}
                    {footer && <div className={cn(styles.footer, footerClassName)}>{footer}</div>}
                </div>
            </div>
        );
    }
);

BottomSheets.displayName = 'BottomSheet';

export default BottomSheets;
